import type { AttendanceType } from '@src/models/interfaces/attendanceEvent.interface';
import { ATTENDANCE_TYPE } from '@src/models/interfaces/attendanceEvent.interface';
import type {
  AttendanceJourneyTimelineItem,
  AttendanceJourneyTimelineItemType,
  CompletedMarking,
} from '@src/models/interfaces/attendanceJourneyTimeline.interface';
import type { UserWorkJourneyScheduleDetails } from '@src/models/interfaces/userWorkJourneySchedule.interface';
import { DateUtil } from '@src/packages/utils/dates';

/** Strict journey marking order used by portal + mark-attendance drawer. */
export const MARKING_SEQUENCE: readonly AttendanceType[] = [
  ATTENDANCE_TYPE.SHIFT_START,
  ATTENDANCE_TYPE.BREAK_START,
  ATTENDANCE_TYPE.BREAK_END,
  ATTENDANCE_TYPE.SHIFT_END,
];

const TIMELINE_TYPE_BY_MARKING: Record<AttendanceType, AttendanceJourneyTimelineItemType> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'initial',
  [ATTENDANCE_TYPE.BREAK_START]: 'breakStart',
  [ATTENDANCE_TYPE.BREAK_END]: 'breakEnd',
  [ATTENDANCE_TYPE.SHIFT_END]: 'final',
  [ATTENDANCE_TYPE.SHIFT_CHANGES]: 'final',
};

const PENDING_LABEL_BY_MARKING: Record<AttendanceType, string> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'markingPortal.marking.timeline.notStarted',
  [ATTENDANCE_TYPE.BREAK_START]: 'markingPortal.marking.timeline.notStarted',
  [ATTENDANCE_TYPE.BREAK_END]: 'markingPortal.marking.timeline.notFinished',
  [ATTENDANCE_TYPE.SHIFT_END]: 'markingPortal.marking.timeline.notFinished',
  [ATTENDANCE_TYPE.SHIFT_CHANGES]: 'markingPortal.marking.timeline.notFinished',
};

const MARKING_COMPLETED_AT_FORMAT = DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT;
const MARKING_SCHEDULED_AT_FORMAT = DateUtil.DEFAULT_TIME_FORMAT;

export type AttendanceJourneyScheduledAtByType = Partial<Record<AttendanceType, number | null>>;

const formatClockTime = (
  at: number | null | undefined,
  format: string,
  timeZone?: string | null,
): string | undefined => {
  if (at == null) {
    return undefined;
  }
  return DateUtil.toDateString(at, format, timeZone || null);
};

const secondsToMs = (seconds: number | null | undefined): number | null =>
  typeof seconds === 'number' ? seconds * 1000 : null;

export const getScheduledAtByTypeFromScheduleDetails = (
  details: Pick<
    UserWorkJourneyScheduleDetails,
    | 'scheduledStartAtSeconds'
    | 'scheduledEndAtSeconds'
    | 'scheduledBreakStartAtSeconds'
    | 'scheduledBreakEndAtSeconds'
  > | null,
): AttendanceJourneyScheduledAtByType => ({
  [ATTENDANCE_TYPE.SHIFT_START]: secondsToMs(details?.scheduledStartAtSeconds),
  [ATTENDANCE_TYPE.BREAK_START]: secondsToMs(details?.scheduledBreakStartAtSeconds),
  [ATTENDANCE_TYPE.BREAK_END]: secondsToMs(details?.scheduledBreakEndAtSeconds),
  [ATTENDANCE_TYPE.SHIFT_END]: secondsToMs(details?.scheduledEndAtSeconds),
});

export const getScheduledAtByTypeFromMillis = (times?: {
  startAt?: number | null;
  breakStartAt?: number | null;
  breakEndAt?: number | null;
  endAt?: number | null;
} | null): AttendanceJourneyScheduledAtByType => ({
  [ATTENDANCE_TYPE.SHIFT_START]: times?.startAt ?? null,
  [ATTENDANCE_TYPE.BREAK_START]: times?.breakStartAt ?? null,
  [ATTENDANCE_TYPE.BREAK_END]: times?.breakEndAt ?? null,
  [ATTENDANCE_TYPE.SHIFT_END]: times?.endAt ?? null,
});

/**
 * Builds the four-step journey timeline from completed markings.
 * Shared by Marking Portal and Mark Attendance drawer.
 */
export const buildAttendanceJourneyTimeline = (
  completed: readonly CompletedMarking[],
  timeZone?: string | null,
  scheduledAtByType?: AttendanceJourneyScheduledAtByType | null,
): AttendanceJourneyTimelineItem[] => {
  const completedByType = new Map(completed.map(mark => [mark.type, mark]));

  return MARKING_SEQUENCE.map(type => {
    const mark = completedByType.get(type);
    if (mark) {
      return {
        type: TIMELINE_TYPE_BY_MARKING[type],
        status: 'completed',
        completedAt: formatClockTime(mark.at, MARKING_COMPLETED_AT_FORMAT, timeZone),
      };
    }

    return {
      type: TIMELINE_TYPE_BY_MARKING[type],
      status: 'pending',
      scheduledAt: formatClockTime(
        scheduledAtByType?.[type],
        MARKING_SCHEDULED_AT_FORMAT,
        timeZone,
      ),
      pendingStatusLabelId: PENDING_LABEL_BY_MARKING[type],
    };
  });
};

/**
 * Reads completed markings from schedule-details actual timestamps.
 * Stops at the first gap (same rule as attendance-record denormalized fields).
 * SHIFT_END is not on schedule details — pass `hasShiftEnd` when known from events.
 */
export const getCompletedMarkingsFromScheduleDetails = (
  details: Pick<
    UserWorkJourneyScheduleDetails,
    'actualStartAtSeconds' | 'actualBreakStartAtSeconds' | 'actualBreakEndAtSeconds'
  > | null,
  options?: { hasShiftEnd?: boolean; shiftEndAtMs?: number | null },
): CompletedMarking[] => {
  if (!details) {
    return [];
  }

  const actualByMarking: Partial<Record<AttendanceType, number | null>> = {
    [ATTENDANCE_TYPE.SHIFT_START]: secondsToMs(details.actualStartAtSeconds),
    [ATTENDANCE_TYPE.BREAK_START]: secondsToMs(details.actualBreakStartAtSeconds),
    [ATTENDANCE_TYPE.BREAK_END]: secondsToMs(details.actualBreakEndAtSeconds),
  };

  const completed: CompletedMarking[] = [];
  for (const type of MARKING_SEQUENCE) {
    if (type === ATTENDANCE_TYPE.SHIFT_END) {
      if (!options?.hasShiftEnd) {
        break;
      }
      completed.push({ type, at: options.shiftEndAtMs ?? null });
      continue;
    }

    const millis = actualByMarking[type];
    if (millis == null) {
      break;
    }
    completed.push({ type, at: millis });
  }

  return completed;
};

/**
 * Merges two completed-marking lists following MARKING_SEQUENCE.
 * Prefers a non-null `at` when both sides have the same type; stops at the first gap.
 */
export const mergeCompletedMarkings = (
  primary: readonly CompletedMarking[],
  secondary: readonly CompletedMarking[],
): CompletedMarking[] => {
  const byType = new Map<AttendanceType, CompletedMarking>();

  for (const mark of [...primary, ...secondary]) {
    const prev = byType.get(mark.type);
    if (!prev || (prev.at == null && mark.at != null)) {
      byType.set(mark.type, mark);
    }
  }

  const completed: CompletedMarking[] = [];
  for (const type of MARKING_SEQUENCE) {
    const mark = byType.get(type);
    if (!mark) {
      break;
    }
    completed.push(mark);
  }
  return completed;
};

/**
 * Builds sequential completed markings from a type→timestamp map, stopping at the first gap.
 * Use `Map` so absent keys are distinct from present keys with `null` timestamps.
 */
export const getCompletedMarkingsFromTypeTimestamps = (
  actualByMarking: ReadonlyMap<AttendanceType, number | null>,
): CompletedMarking[] => {
  const completed: CompletedMarking[] = [];
  for (const type of MARKING_SEQUENCE) {
    if (!actualByMarking.has(type)) {
      break;
    }
    completed.push({ type, at: actualByMarking.get(type) ?? null });
  }
  return completed;
};
